import { useMemo, useState } from "react";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  addCustomFieldIssues,
  BaseForm,
  buildCustomFieldDefaults,
  buildCustomFieldSelection,
  customFieldsPayload,
  customFieldsSchema,
  CustomFieldsSection,
  FormInput,
  FormTag,
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import tagConstants from "constants/Tag";
import EmailTemplateService from "services/EmailTemplate";
import EmailTemplatePreviewDialog from "routes/email/components/EmailTemplatePreviewDialog";
import {
  decodeTemplateHtml,
  fetchLatestInfo,
} from "routes/email/emailTemplateCache";

const MODEL_TYPE = tagConstants.taggableModels.email_template;
const CUSTOM_FIELD_MODEL = "EmailTemplate";

const buildSchema = configs =>
  z
    .object({
      name: z.string().trim().min(1, "Name is required"),
      subject: z.string().trim().min(1, "Subject is required"),
      preview_text: z.string(),
      tags: z.array(z.any()),
      custom_fields: customFieldsSchema,
    })
    .superRefine((values, ctx) =>
      addCustomFieldIssues(configs, values.custom_fields, ctx)
    );

// Only revealed fields are sent. A field the user removed keeps its value in
// form state, and leaving it out of the payload is what clears it.
const revealedValues = (customFields, selection) => {
  const revealed = new Set(
    selection.filter(c => c.required || c.selected).map(c => c.label)
  );
  return Object.fromEntries(
    Object.entries(customFields).filter(([label]) => revealed.has(label))
  );
};

const GeneralEmailTemplateFields = ({
  template,
  configs,
  groups,
  mode,
  onSaved,
  onCancel,
}) => {
  const [selection, setSelection] = useState(() =>
    buildCustomFieldSelection(
      configs,
      template ? (template.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(() => buildSchema(configs), [configs]);

  const defaultValues = useMemo(
    () => ({
      // A clone starts unnamed so the user is forced to pick a distinct name.
      name: mode === "clone" ? "" : (template?.name ?? ""),
      subject: template?.subject ?? "",
      preview_text: template?.info?.preview_text ?? "",
      tags: template?.tags ?? [],
      custom_fields: buildCustomFieldDefaults(configs, template?.custom_fields),
    }),
    [template, configs, mode]
  );

  const previewHtml = decodeTemplateHtml(template);

  const handleSubmit = async ({
    name,
    subject,
    preview_text,
    tags,
    custom_fields,
  }) => {
    const storedInfo =
      mode === "edit" ? await fetchLatestInfo(template.id) : template?.info;
    const info = { ...storedInfo, preview_text };
    const customFields = customFieldsPayload(
      configs,
      revealedValues(custom_fields, selection)
    );
    const saved =
      mode === "edit"
        ? await EmailTemplateService.editEmailTemplate({
            id: template.id,
            name,
            subject,
            info,
            customFields,
            tags,
          })
        : await EmailTemplateService.createEmailTemplate(
            name,
            subject,
            null,
            // A clone carries the source body, so it is sendable before the
            // user revisits the Graphical tab.
            previewHtml || null,
            info,
            customFields,
            tags
          );
    await onSaved(saved);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitButtonText="Save & Continue"
      extraButtons={
        previewHtml
          ? [
              <EmailTemplatePreviewDialog
                key="preview"
                html={previewHtml}
                name={template?.name}
              />,
            ]
          : undefined
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:flex tw:flex-col tw:gap-5">
            <FormInput name="name" label="Name" required />
            <FormInput name="subject" label="Subject" required />
            <FormInput name="preview_text" label="Preview Text" />
            <FormTag name="tags" modelType={MODEL_TYPE} />
          </div>
        </CardContent>
      </Card>

      {selection.length > 0 && (
        <CustomFieldsSection
          selection={selection}
          setSelection={setSelection}
          groups={groups}
        />
      )}
    </BaseForm>
  );
};

/**
 * Name, subject, preview text, tags, and custom fields. On a new template or a
 * clone this is the step that creates the record the other tabs edit.
 */
const GeneralEmailTemplate = props => {
  const { data: configs, isFetched: configsFetched } =
    useCustomFieldConfigs(CUSTOM_FIELD_MODEL);
  const { data: groups, isFetched: groupsFetched } =
    useCustomFieldGroups(CUSTOM_FIELD_MODEL);

  // The schema and the custom-field defaults are built from the configs, and
  // react-hook-form latches defaultValues on mount, so the form waits for them.
  if (!configsFetched || !groupsFetched) {
    return (
      <div className="tw:p-8 tw:text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <GeneralEmailTemplateFields
      {...props}
      configs={configs ?? []}
      groups={groups ?? []}
    />
  );
};

export default GeneralEmailTemplate;
